import { addDays, formatShortDate } from './dates';
import { seriesRule } from './history';
import { MAX_SERIES_OCCURRENCES, isSkipped, patternDates } from './recurrence';
import type { EventDraft } from './transitions';
import type { Ministry, ScheduleEvent } from './types';

/**
 * Os eventos como a página Eventos os mostra: um card por série (a semanal ou o evento
 * único com as datas dele). **Ativo** tem alguma data de hoje em diante; **inativo** já
 * aconteceu e não tem data à frente — continua listado para consultar, duplicar ou ganhar
 * uma nova data.
 */
export interface EventSeriesGroup {
  seriesId: string;
  /** As datas de hoje em diante, da mais próxima à mais distante. */
  upcoming: ScheduleEvent[];
  /** As que já passaram, da mais recente à mais antiga. */
  past: ScheduleEvent[];
  /** A configuração da série: a última data que não foi alterada sozinha. */
  template: ScheduleEvent;
}

export interface EventSeriesGroups {
  /** Pela próxima data. */
  active: EventSeriesGroup[];
  /** Pela última data, da mais recente à mais antiga. */
  inactive: EventSeriesGroup[];
}

const key = (e: ScheduleEvent) => e.date + e.time;

export function eventSeriesGroups(events: readonly ScheduleEvent[], today: string): EventSeriesGroups {
  const bySeries = new Map<string, ScheduleEvent[]>();

  for (const e of events) bySeries.set(e.seriesId, [...(bySeries.get(e.seriesId) ?? []), e]);

  const groups = [...bySeries.entries()].map(([seriesId, list]): EventSeriesGroup => {
    const sorted = [...list].sort((a, b) => key(a).localeCompare(key(b)));

    return {
      seriesId,
      upcoming: sorted.filter((e) => e.date >= today),
      past: sorted.filter((e) => e.date < today).reverse(),
      template: sorted.filter((e) => !e.edited).at(-1) ?? sorted.at(-1)!,
    };
  });

  return {
    active: groups
      .filter((g) => g.upcoming.length)
      .sort((a, b) => key(a.upcoming[0]!).localeCompare(key(b.upcoming[0]!))),
    inactive: groups
      .filter((g) => !g.upcoming.length)
      .sort((a, b) => key(b.past[0]!).localeCompare(key(a.past[0]!))),
  };
}

/**
 * "Duplicar": o rascunho do evento novo com a configuração de outro — título, cor, horário,
 * ministérios e quantidades por função. A data e a recorrência ficam as do rascunho, a
 * escala não vem (é montada depois) e o que não existe mais fica de fora. O evento novo
 * ganha o seu próprio card; para outra data do mesmo evento, use a nova data
 * (`occurrences`).
 */
export function duplicateDraft(
  draft: EventDraft,
  base: ScheduleEvent,
  ministries: readonly Ministry[]
): EventDraft {
  const kept = ministries.filter((m) => base.ministryIds.includes(m.id));
  const roleIds = new Set(kept.flatMap((m) => m.roles.map((r) => r.id)));

  return {
    ...draft,
    mode: 'create',
    eventId: null,
    seriesId: null,
    isRecurring: false,
    scope: 'one',
    title: base.title,
    color: base.color,
    time: base.time,
    ministryIds: base.ministryIds.filter((id) => kept.some((m) => m.id === id)),
    need: Object.fromEntries(Object.entries(base.need).filter(([roleId]) => roleIds.has(roleId))),
  };
}

// ===== Data de término da série =====

export interface SeriesEndPlan {
  /** A última data que a série tem hoje. */
  current: string;
  /** As datas novas (pela regra), depois da última, até o término escolhido. */
  add: string[];
  /** As datas depois do término escolhido, que saem (só as que ainda vão acontecer). */
  remove: ScheduleEvent[];
  /** Das que saem, quantas já têm alguém escalado. */
  scheduled: number;
  error: string | null;
}

const hasPeople = (e: ScheduleEvent) => Object.values(e.slots).some((ids) => ids.length > 0);

/**
 * O que muda ao trocar a data de término de uma série que se repete: estender cria as
 * datas da regra depois da última (até `MAX_SERIES_OCCURRENCES` de uma vez); encurtar tira
 * as datas depois do término — nunca antes de hoje, para o histórico não sair.
 */
export function seriesEndPlan(
  events: readonly ScheduleEvent[],
  seriesId: string,
  until: string,
  today: string
): SeriesEndPlan {
  const series = events.filter((e) => e.seriesId === seriesId).sort((a, b) => a.date.localeCompare(b.date));
  const current = series.at(-1)?.date ?? until;
  const rule = seriesRule(events, seriesId);
  const empty = { current, add: [], remove: [], scheduled: 0 };

  if (!rule) return { ...empty, error: 'Só o evento que se repete tem data de término.' };
  if (!until) return { ...empty, error: 'Escolha a data de término.' };
  if (until < today) return { ...empty, error: 'A data de término não pode ser antes de hoje.' };

  const existing = new Set(series.map((e) => e.date));
  const add =
    until > current
      ? patternDates(rule, addDays(current, 1), until).filter((d) => !existing.has(d) && !isSkipped(rule, d))
      : [];
  const remove = series.filter((e) => e.date > until);
  const scheduled = remove.filter(hasPeople).length;

  if (add.length > MAX_SERIES_OCCURRENCES)
    return {
      ...empty,
      error: `Passa de ${MAX_SERIES_OCCURRENCES} datas novas de uma vez: escolha um término mais próximo.`,
    };
  if (remove.length === series.length)
    return { ...empty, error: 'O término tira todas as datas: exclua a série.' };

  return { current, add, remove, scheduled, error: null };
}

/** "Cria 52 datas novas, de 3 jan a 26 dez." / "Remove 4 datas, 1 com escala." / "" sem mudança. */
export function seriesEndSummary(plan: SeriesEndPlan): string {
  if (plan.error) return plan.error;

  const range = (dates: readonly string[]) =>
    dates.length === 1
      ? formatShortDate(dates[0]!)
      : `de ${formatShortDate(dates[0]!)} a ${formatShortDate(dates.at(-1)!)}`;

  if (plan.add.length)
    return `Cria ${plan.add.length} ${plan.add.length === 1 ? 'data nova' : 'datas novas'}: ${range(plan.add)}.`;
  if (plan.remove.length) {
    const n = plan.remove.length;
    const people = plan.scheduled
      ? `; ${plan.scheduled} ${plan.scheduled === 1 ? 'tem' : 'têm'} escala, e quem está nela sai`
      : '';

    return `Remove ${n} ${n === 1 ? 'data' : 'datas'} (${range(plan.remove.map((e) => e.date))})${people}.`;
  }

  return '';
}

/**
 * Troca a data de término: cria as datas novas com a configuração da série (a última data
 * não alterada sozinha) e o id `{série}-{data}`, tira as que passam do término e grava o
 * fim (`end` "até a data") na regra de todas. Sem mudança válida, devolve como estava.
 */
export function changeSeriesEnd(
  events: ScheduleEvent[],
  seriesId: string,
  until: string,
  today: string
): ScheduleEvent[] {
  const plan = seriesEndPlan(events, seriesId, until, today);
  const rule = seriesRule(events, seriesId);

  if (plan.error || !rule || (!plan.add.length && !plan.remove.length)) return events;

  const series = events.filter((e) => e.seriesId === seriesId);
  const template =
    series
      .filter((e) => !e.edited)
      .sort((a, b) => a.date.localeCompare(b.date))
      .at(-1) ?? series[0]!;
  const ruled = { ...rule, end: { kind: 'until' as const, date: until } };
  const ids = new Set(events.map((e) => e.id));
  const gone = new Set(plan.remove.map((e) => e.id));
  const created = plan.add.map((date): ScheduleEvent => {
    let id = `${seriesId}-${date}`;

    for (let n = 2; ids.has(id); n++) id = `${seriesId}-${date}-${n}`;

    return {
      ...template,
      id,
      date,
      rule: ruled,
      slots: {},
      generated: false,
      published: false,
      edited: false,
      responses: {},
      attendance: {},
      nudgedAt: null,
      recorded: false,
      notices: {},
      swaps: [],
      audience: null,
    };
  });

  return [
    ...events
      .filter((e) => !gone.has(e.id))
      .map((e) => (e.seriesId === seriesId ? { ...e, rule: ruled } : e)),
    ...created,
  ].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
}
